import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { MainLayout } from "../components/MainLayout";
import { RampCard } from "../components/RampCard";
import { SearchableSelect } from "../components/ui/SearchableSelect";
import { api } from "../lib/api-client";
import { useAuth } from "../contexts/AuthContext";
import {
  PouchPayClient,
  SessionResponse,
  KycRequirement,
  BankNetwork,
  KycField,
} from "@pouchpay/sdk";

type SessionMode = "ONRAMP" | "OFFRAMP";
type FlowStep = "BANK_ACCOUNT" | "KYC_ID" | "KYC" | "AWAIT_PAYMENT";

export function RampPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize sessionId from URL params
  const [sessionId, setSessionId] = useState<string | null>(
    searchParams.get("sessionId"),
  );
  const [activeTab, setActiveTab] = useState<SessionMode>(
    (searchParams.get("type") as SessionMode) || "ONRAMP",
  );
  const [formData, setFormData] = useState({
    walletAddress: "",
    walletTag: "",
    amount: "10000",
    cryptoAmount: "100",
    cryptoCurrency: "USDT",
    cryptoNetwork: "TRC20",
    countryCode: "NG",
    currency: "NGN",
  });
  const [pendingOfframpData, setPendingOfframpData] = useState<any>(null);
  const [offrampResponse, setOfframpResponse] = useState<any>(null);
  const [offrampStatus, setOfframpStatus] = useState<string>("PENDING");
  const [onrampResponse, setOnrampResponse] = useState<any>(null);
  const [onrampStatus, setOnrampStatus] = useState<string>("PENDING");
  const [transactionHash, setTransactionHash] = useState<string>("");
  const [timeRemaining, setTimeRemaining] = useState<string>("");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState("");
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);

  // Flow state
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [currentStep, setCurrentStep] = useState<FlowStep | null>(null);
  const [localKycStep, setLocalKycStep] = useState<"KYC_ID" | "KYC" | null>(
    null,
  );
  const localKycStepRef = useRef<"KYC_ID" | "KYC" | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Bank account state
  const [bankNetworks, setBankNetworks] = useState<BankNetwork[]>([]);

  // Crypto channels state
  const [cryptoChannels, setCryptoChannels] = useState<any>(null);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [savedBankAccounts, setSavedBankAccounts] = useState<any[]>([]);
  const [selectedSavedAccountId, setSelectedSavedAccountId] = useState("");
  const [showAddNew, setShowAddNew] = useState(false);
  const [selectedNetworkId, setSelectedNetworkId] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankError, setBankError] = useState("");
  const [verifiedBankAccount, setVerifiedBankAccount] = useState<any>(null);
  const [isVerifyingBank, setIsVerifyingBank] = useState(false);
  const bankNetworksFetchedRef = useRef(false);
  const savedAccountsFetchedRef = useRef(false);

  // KYC state
  const [kycRequirement, setKycRequirement] = useState<KycRequirement | null>(
    null,
  );
  const [kycDocs, setKycDocs] = useState<Record<string, string>>({});
  const [kycError, setKycError] = useState("");
  const kycRequirementFetchedRef = useRef(false);

  // KYC verification status
  const [kycStatus, setKycStatus] = useState<string | null>(null);
  const [checkingKyc, setCheckingKyc] = useState(true);

  // Transaction limits state
  const [transactionLimits, setTransactionLimits] = useState<any>(null);
  const [loadingLimits, setLoadingLimits] = useState(true);

  // Check KYC status and load limits on mount
  useEffect(() => {
    const checkKycStatus = async () => {
      try {
        const response = await api.kyc.status({
          countryCode: formData.countryCode,
          providerId: "yellowcard",
        });

        const status = response.data?.status;
        setKycStatus(status);

        // No longer redirect non-KYC users - allow them to transact with limits
      } catch (err) {
        console.error("Failed to check KYC status:", err);
        // If we can't check status, assume no KYC but allow transaction
        setKycStatus(null);
      } finally {
        setCheckingKyc(false);
      }
    };

    checkKycStatus();
  }, [formData.countryCode, navigate]);

  // Function to fetch/refresh transaction limits
  const refreshLimits = async () => {
    try {
      setLoadingLimits(true);
      const response = await api.ramp.getLimits();
      setTransactionLimits(response.data);
    } catch (err) {
      console.error("Failed to fetch transaction limits:", err);
    } finally {
      setLoadingLimits(false);
    }
  };

  // Fetch transaction limits on mount
  useEffect(() => {
    refreshLimits();
  }, []);

  // Create SDK client
  const sdkClient = useMemo(() => {
    const token = localStorage.getItem("user_access_token");
    const apiKey = import.meta.env.VITE_API_KEY;

    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    if (apiKey) {
      headers["x-api-key"] = apiKey;
    }

    return new PouchPayClient({
      baseUrl: import.meta.env.VITE_API_BASE_URL || "http://localhost:3002",
      headers: Object.keys(headers).length > 0 ? headers : undefined,
    });
  }, []);

  // Fetch crypto channels on mount
  useEffect(() => {
    const fetchCryptoChannels = async () => {
      setLoadingChannels(true);
      try {
        console.log("Fetching crypto channels...");
        const response = await api.crypto.channels();
        console.log("Crypto channels response:", response.data);
        setCryptoChannels(response.data);
      } catch (err) {
        console.error("Failed to fetch crypto channels:", err);
        console.error("Error details:", err);
      } finally {
        setLoadingChannels(false);
      }
    };

    fetchCryptoChannels();
  }, []);

  // Restore transaction from URL params on mount
  useEffect(() => {
    const providerRef = searchParams.get("ref");
    const type = searchParams.get("type");
    if (!providerRef || offrampResponse || onrampResponse) return; // Skip if already loaded

    const restoreTransaction = async () => {
      try {
        console.log("Restoring transaction from URL:", providerRef, type);
        const rampType = type === "onramp" ? "ONRAMP" : "OFFRAMP";
        const response = await api.ramp.checkStatus(providerRef, rampType);
        console.log("Restored transaction:", response.data);

        if (type === "onramp" || response.data.type === "ONRAMP") {
          // Reconstruct onrampResponse from status response
          setOnrampResponse({
            providerRef: response.data.providerRef,
            paymentInstruction:
              response.data.details?.paymentInstruction ||
              response.data.settlementInfo,
          });
          setOnrampStatus(response.data.status);
          setActiveTab("ONRAMP");
        } else {
          // Reconstruct offrampResponse from status response
          setOfframpResponse({
            providerRef: response.data.providerRef,
            cryptoInstruction: response.data.settlementInfo,
          });
          setOfframpStatus(response.data.status);
          setActiveTab("OFFRAMP");
        }

        setCurrentStep("AWAIT_PAYMENT");

        if (response.data.transactionHash) {
          setTransactionHash(response.data.transactionHash);
        }
      } catch (error) {
        console.error("Failed to restore transaction:", error);
        // Clear invalid ref from URL
        setSearchParams({});
      }
    };

    restoreTransaction();
  }, [searchParams, offrampResponse, onrampResponse]);

  // Countdown timer for offramp expiration
  useEffect(() => {
    if (!offrampResponse?.cryptoInstruction?.expiresAt) return;

    const updateTimer = () => {
      const expiresAt = new Date(offrampResponse.cryptoInstruction.expiresAt);
      const now = new Date();
      const diff = expiresAt.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeRemaining("Expired");
        setOfframpStatus("EXPIRED");
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [offrampResponse]);

  // Countdown timer for onramp expiration
  useEffect(() => {
    if (!onrampResponse?.paymentInstruction?.expiresAt) return;

    const updateTimer = () => {
      const expiresAt = new Date(onrampResponse.paymentInstruction.expiresAt);
      const now = new Date();
      const diff = expiresAt.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeRemaining("Expired");
        setOnrampStatus("EXPIRED");
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [onrampResponse]);

  // Poll for offramp status
  useEffect(() => {
    if (
      !offrampResponse?.providerRef ||
      offrampStatus === "EXPIRED" ||
      offrampStatus === "COMPLETED"
    )
      return;

    const pollStatus = async () => {
      try {
        const response = await api.ramp.checkStatus(
          offrampResponse.providerRef,
          "OFFRAMP",
        );
        console.log("Offramp status:", response.data);
        setOfframpStatus(response.data.status);

        // Update transaction hash if available
        if (response.data.transactionHash) {
          setTransactionHash(response.data.transactionHash);
        }
      } catch (error) {
        console.error("Failed to check offramp status:", error);
      }
    };

    // Poll immediately and then every 10 seconds
    pollStatus();
    const interval = setInterval(pollStatus, 10000);
    return () => clearInterval(interval);
  }, [offrampResponse, offrampStatus]);

  // Poll for onramp status
  useEffect(() => {
    if (
      !onrampResponse?.providerRef ||
      onrampStatus === "EXPIRED" ||
      onrampStatus === "COMPLETED"
    )
      return;

    const pollStatus = async () => {
      try {
        const response = await api.ramp.checkStatus(
          onrampResponse.providerRef,
          "ONRAMP",
        );
        console.log("Onramp status:", response.data);
        setOnrampStatus(response.data.status);

        // Update transaction hash if available
        if (response.data.transactionHash) {
          setTransactionHash(response.data.transactionHash);
        }
      } catch (error) {
        console.error("Failed to check onramp status:", error);
      }
    };

    // Poll immediately and then every 10 seconds
    pollStatus();
    const interval = setInterval(pollStatus, 10000);
    return () => clearInterval(interval);
  }, [onrampResponse, onrampStatus]);

  // Clear URL params when transaction reaches terminal state
  useEffect(() => {
    const terminalStates = ["COMPLETED", "EXPIRED", "FAILED", "CANCELLED"];
    if (
      (terminalStates.includes(offrampStatus) ||
        terminalStates.includes(onrampStatus)) &&
      searchParams.get("ref")
    ) {
      console.log("Transaction reached terminal state, clearing URL params");
      setSearchParams({});
    }
  }, [offrampStatus, onrampStatus, searchParams, setSearchParams]);

  // Compute available currencies
  const availableCurrencies = useMemo(() => {
    if (!cryptoChannels?.channels) {
      console.log("No crypto channels available");
      return [];
    }
    const filtered = cryptoChannels.channels
      // If validationStatus exists, check it. Otherwise just check enabled.
      .filter(
        (channel: any) =>
          channel.enabled &&
          (!channel.validationStatus ||
            channel.validationStatus === "validated"),
      )
      .map((channel: any) => ({
        code: channel.code,
        name: channel.name || channel.code,
        defaultNetwork: channel.defaultNetwork,
        supportedNetworks: channel.supportedNetworks || [],
      }));
    console.log("Available currencies:", filtered);
    return filtered;
  }, [cryptoChannels]);

  // Compute available networks for selected currency
  const availableNetworks = useMemo(() => {
    if (!cryptoChannels?.channels || !formData.cryptoCurrency) {
      console.log("Cannot compute networks - missing data");
      return [];
    }

    const selectedChannel = cryptoChannels.channels.find(
      (ch: any) => ch.code === formData.cryptoCurrency,
    );

    console.log(
      "Selected channel for",
      formData.cryptoCurrency,
      ":",
      selectedChannel,
    );

    if (!selectedChannel || !selectedChannel.networks) {
      console.log("No networks found for selected channel");
      return [];
    }

    // Get networks that are validated and enabled
    // If 'validated' field exists (new API), use it. Otherwise (old API), show all networks.
    const networks = Object.values(selectedChannel.networks)
      .filter((network: any) => {
        // If validated field exists, it means we're using the new API with static validation
        if ("validated" in network) {
          return network.validated === true && network.enabled !== false;
        }
        // Old API without validation - show all networks regardless of enabled flag
        // (we'll rely on backend validation when session is created)
        return true;
      })
      .map((network: any) => ({
        code: network.network,
        name: network.name || network.network,
        requiresMemo: network.requiresMemo || false,
        explorerUrl: network.explorerUrl || "",
      }));

    console.log(
      "Available networks for",
      formData.cryptoCurrency,
      ":",
      networks,
    );
    return networks;
  }, [cryptoChannels, formData.cryptoCurrency]);

  // Available fiat currencies
  const availableFiatCurrencies = [
    { code: "NGN", name: "Nigerian Naira", symbol: "₦" },
    // Future: Add more as backend supports them
    // { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
    // { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵' },
  ];

  // Get explorer URL for current network
  // Tries to use network from transaction payload first, falls back to formData
  const getExplorerLink = (
    txHash: string,
    networkCode?: string,
  ): string | null => {
    if (!txHash) return null;

    // Determine which network to use:
    // 1. Explicit networkCode parameter (from transaction payload)
    // 2. formData.cryptoNetwork (user's current selection)
    const targetNetwork = networkCode || formData.cryptoNetwork;
    if (!targetNetwork) return null;

    const network = availableNetworks.find((n) => n.code === targetNetwork);
    if (!network?.explorerUrl) return null;

    // Replace placeholder with actual tx hash
    return network.explorerUrl
      .replace("{txHash}", txHash)
      .replace("{hash}", txHash);
  };

  // Auto-select default network when currency changes
  useEffect(() => {
    if (!formData.cryptoCurrency || !cryptoChannels?.channels) return;

    const selectedChannel = cryptoChannels.channels.find(
      (ch: any) => ch.code === formData.cryptoCurrency,
    );

    if (selectedChannel?.defaultNetwork) {
      // Check if current network is valid for this currency
      const isCurrentNetworkValid = availableNetworks.some(
        (net: any) => net.code === formData.cryptoNetwork,
      );

      // If current network is not valid, switch to default
      if (!isCurrentNetworkValid) {
        setFormData((prev) => ({
          ...prev,
          cryptoNetwork: selectedChannel.defaultNetwork,
        }));
      }
    }
  }, [formData.cryptoCurrency, cryptoChannels, availableNetworks]);

  // Sync sessionId and type to URL params (for SDK-based flow)
  // Note: Stateless flow (onramp/offramp) uses 'ref' param instead, set directly in handlers
  useEffect(() => {
    // Only manage sessionId param if we're in SDK-based flow
    // Don't clear URL params if we have a 'ref' param (stateless flow)
    const currentRef = searchParams.get("ref");

    if (sessionId && sessionId !== "pending-offramp") {
      setSearchParams({ sessionId, type: activeTab });
    } else if (!currentRef) {
      // Only clear if we don't have a stateless transaction reference
      setSearchParams({});
    }
  }, [sessionId, activeTab, setSearchParams, searchParams]);

  // Fetch bank networks for pending offramp
  useEffect(() => {
    const fetchBankNetworksForPendingOfframp = async () => {
      if (
        sessionId === "pending-offramp" &&
        currentStep === "BANK_ACCOUNT" &&
        bankNetworks.length === 0
      ) {
        try {
          // Fetch bank networks from API
          const response = await api.ramp.getBankNetworks(
            pendingOfframpData?.countryCode || "NG",
          );
          setBankNetworks(response.data.networks || []);

          // Fetch saved bank accounts
          if (!savedAccountsFetchedRef.current) {
            try {
              const response = await api.bankAccounts.list();
              const accounts = response.data.accounts || [];
              setSavedBankAccounts(accounts);
              savedAccountsFetchedRef.current = true;

              if (accounts.length === 0) {
                setShowAddNew(true);
              }
            } catch (err) {
              setShowAddNew(true);
            }
          }
        } catch (err) {
          console.error("Failed to fetch bank data:", err);
        }
      }
    };

    fetchBankNetworksForPendingOfframp();
  }, [sessionId, currentStep, bankNetworks.length]);

  const handleTabChange = (tab: SessionMode) => {
    setActiveTab(tab);
    setSessionId(null);
    setSession(null);
    setCurrentStep(null);
    setError("");
    setOfframpResponse(null);
    setOnrampResponse(null);
    setPendingOfframpData(null);
    setOfframpStatus("PENDING");
    setOnrampStatus("PENDING");
    setTransactionHash("");
    setSearchParams({}); // Clear URL params
    // Reset fetch flags for new session
    bankNetworksFetchedRef.current = false;
    kycRequirementFetchedRef.current = false;
    localKycStepRef.current = null;
    setLocalKycStep(null);
    refreshLimits(); // Refresh limits when switching tabs
    // Note: Form data is preserved when switching modes
  };

  const handleCreateTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsCreating(true);

    try {
      if (activeTab === "ONRAMP") {
        const localAmount = parseFloat(formData.amount);

        // Send the LOCAL amount + its currency. The API's `amount` is denominated
        // in `currency` and it converts local->USD once server-side (same rate
        // source as /crypto/rate). Do NOT pre-convert to USD here while still
        // tagging currency as NGN — the backend would convert a second time
        // (local->USD->/buyRate again), collapsing the amount to ~$0 and the
        // provider rejects it ("check the amount").
        const response = await api.ramp.onramp({
          amount: localAmount,
          cryptoCurrency: formData.cryptoCurrency,
          cryptoNetwork: formData.cryptoNetwork,
          walletAddress: formData.walletAddress,
          walletTag: formData.walletTag || undefined,
          countryCode: formData.countryCode,
          currency: formData.currency,
        });

        // Onramp created successfully - store response and show payment instructions
        setOnrampResponse(response.data);
        setError("");
        setCurrentStep("AWAIT_PAYMENT"); // Show payment instructions

        // Add providerRef to URL params for persistence
        setSearchParams({ ref: response.data.providerRef, type: "onramp" });
      } else {
        // For offramp, we need to collect bank account first
        // Store the form data for later use
        setPendingOfframpData({
          cryptoAmount: parseFloat(formData.cryptoAmount),
          cryptoCurrency: formData.cryptoCurrency,
          cryptoNetwork: formData.cryptoNetwork,
          countryCode: formData.countryCode,
          currency: formData.currency,
        });

        // Show bank account collection form
        setCurrentStep("BANK_ACCOUNT");
        setSessionId("pending-offramp"); // Placeholder to trigger bank account form
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to create transaction",
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateSession = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsCreating(true);

    try {
      if (activeTab === "ONRAMP") {
        const localAmount = parseFloat(formData.amount);

        // Send the LOCAL amount + its currency. The API's `amount` is denominated
        // in `currency` and it converts local->USD once server-side (same rate
        // source as /crypto/rate). Do NOT pre-convert to USD here while still
        // tagging currency as NGN — the backend would convert a second time
        // (local->USD->/buyRate again), collapsing the amount to ~$0 and the
        // provider rejects it ("check the amount").
        const response = await api.ramp.onramp({
          amount: localAmount,
          cryptoCurrency: formData.cryptoCurrency,
          cryptoNetwork: formData.cryptoNetwork,
          walletAddress: formData.walletAddress,
          walletTag: formData.walletTag || undefined,
          countryCode: formData.countryCode,
          currency: formData.currency,
        });

        // Onramp created successfully - store response and show payment instructions
        setOnrampResponse(response.data);
        setError("");
        setCurrentStep("AWAIT_PAYMENT"); // Show payment instructions

        // Add providerRef to URL params for persistence
        setSearchParams({ ref: response.data.providerRef, type: "onramp" });
      } else {
        // For offramp, we need to collect bank account first
        // Store the form data for later use
        setPendingOfframpData({
          cryptoAmount: parseFloat(formData.cryptoAmount),
          cryptoCurrency: formData.cryptoCurrency,
          cryptoNetwork: formData.cryptoNetwork,
          countryCode: formData.countryCode,
          currency: formData.currency,
        });

        // Show bank account collection form
        setCurrentStep("BANK_ACCOUNT");
        setSessionId("pending-offramp"); // Placeholder to trigger bank account form
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to create transaction",
      );
    } finally {
      setIsCreating(false);
    }
  };

  const refreshSession = async (id?: string) => {
    const sid = id || sessionId;
    if (!sid) return;

    // Skip SDK fetch for placeholder offramp sessions
    if (sid === "pending-offramp") {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const sessionData = await sdkClient.getSession(sid);
      setSession(sessionData);

      // Determine current step based on state flags
      let newStep: FlowStep;
      if (sessionData.type === "OFFRAMP" && !sessionData.bankAccountProvided) {
        newStep = "BANK_ACCOUNT";
      } else if (!sessionData.kycVerified) {
        // Use local KYC step ref (not state) to avoid stale closure
        newStep = localKycStepRef.current || "KYC_ID";
      } else {
        newStep = "AWAIT_PAYMENT";
      }

      setCurrentStep(newStep);

      // Fetch additional data based on step
      if (sessionData.type === "OFFRAMP" && !sessionData.bankAccountProvided) {
        // Fetch saved bank accounts (only once)
        if (!savedAccountsFetchedRef.current) {
          try {
            const response = await api.bankAccounts.list();
            const accounts = response.data.accounts || [];
            setSavedBankAccounts(accounts);
            savedAccountsFetchedRef.current = true;

            // If no saved accounts, show add new form
            if (accounts.length === 0) {
              setShowAddNew(true);
            }
          } catch (err) {
            // If fetch fails, show add new form
            setShowAddNew(true);
          }
        }

        // Fetch bank networks if needed (only once)
        if (!bankNetworksFetchedRef.current) {
          const networks = await sdkClient.getBankNetworks(sid);
          setBankNetworks(networks);
          bankNetworksFetchedRef.current = true;
        }
      }

      if (!sessionData.kycVerified) {
        // Fetch KYC requirements if needed (only once)
        if (!kycRequirementFetchedRef.current) {
          if (sessionData.kycRequirement) {
            setKycRequirement(sessionData.kycRequirement);
            kycRequirementFetchedRef.current = true;
          } else {
            const req = await sdkClient.getKycRequirements(sid);
            setKycRequirement(req);
            kycRequirementFetchedRef.current = true;
          }
        }
      }
    } catch (err: any) {
      setError(err.message || "Failed to load session");
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh session when sessionId changes
  useEffect(() => {
    if (sessionId) {
      refreshSession();
      // Poll for updates every 5 seconds
      const interval = setInterval(() => refreshSession(), 5000);
      return () => clearInterval(interval);
    }
  }, [sessionId]);

  // Sync localKycStep state to ref to avoid stale closures in interval
  useEffect(() => {
    localKycStepRef.current = localKycStep;
  }, [localKycStep]);

  // Reset local KYC step when KYC is verified
  useEffect(() => {
    if (session?.kycVerified) {
      setLocalKycStep(null);
    }
  }, [session?.kycVerified]);

  // Auto-confirm offramp after KYC to generate crypto deposit address
  useEffect(() => {
    if (!session || !sessionId) return;

    // For offramps: if KYC is verified but no crypto instruction yet, confirm offramp
    if (
      session.type === "OFFRAMP" &&
      session.kycVerified &&
      !session?.cryptoInstruction &&
      !actionLoading
    ) {
      const confirmOfframp = async () => {
        setActionLoading(true);
        try {
          await sdkClient.confirmOfframp(sessionId);
          await refreshSession();
        } catch (err: any) {
          setError(err.message || "Failed to generate deposit address");
        } finally {
          setActionLoading(false);
        }
      };

      confirmOfframp();
    }
  }, [
    session?.type,
    session?.kycVerified,
    session?.cryptoInstruction,
    sessionId,
    actionLoading,
  ]);

  // Step 1: Verify bank account
  const handleVerifyBank = async (e: React.FormEvent) => {
    e.preventDefault();

    setBankError("");
    setIsVerifyingBank(true);

    try {
      let accountNum = accountNumber;
      let networkId = selectedNetworkId;
      let bankName = "";

      // If using a saved account, get its details
      if (selectedSavedAccountId && !showAddNew) {
        const savedAccount = savedBankAccounts.find(
          (acc) => acc.id === selectedSavedAccountId,
        );
        if (savedAccount) {
          accountNum = savedAccount.accountNumber;
          networkId = savedAccount.networkId;
          bankName = savedAccount.bankName;
        }
      } else {
        // Find bank name from networks
        const bank = bankNetworks.find((b) => b.id === networkId);
        bankName = bank?.name || "Unknown Bank";
      }

      if (!accountNum || !networkId) {
        setBankError("Please select or enter a bank account");
        return;
      }

      // Verify bank account
      const verifyResponse = await api.ramp.verifyBank({
        accountNumber: accountNum,
        networkId,
        countryCode: pendingOfframpData?.countryCode || "NG",
        providerId: "yellowcard",
      });

      // Store verified account details
      setVerifiedBankAccount({
        accountNumber: accountNum,
        accountName: verifyResponse.data.accountName,
        bankName: verifyResponse.data.bankName || bankName,
        networkId,
      });
    } catch (err: any) {
      setBankError(
        err.response?.data?.message ||
          err.message ||
          "Failed to verify bank account",
      );
    } finally {
      setIsVerifyingBank(false);
    }
  };

  // Step 2: Confirm and create offramp
  const handleConfirmAndCreateOfframp = async () => {
    if (!verifiedBankAccount || !pendingOfframpData) return;

    setActionLoading(true);
    setBankError("");

    try {
      const response = await api.ramp.offramp({
        ...pendingOfframpData,
        bankAccount: {
          accountNumber: verifiedBankAccount.accountNumber,
          accountName: verifiedBankAccount.accountName,
          networkId: verifiedBankAccount.networkId,
        },
      });

      // Offramp created successfully - store response and show crypto deposit address
      setOfframpResponse(response.data);
      setError("");
      setBankError("");
      setCurrentStep("AWAIT_PAYMENT"); // Show deposit instructions

      // Add providerRef to URL params for persistence
      setSearchParams({ ref: response.data.providerRef });

      // Reset verification state
      setVerifiedBankAccount(null);
    } catch (err: any) {
      setBankError(
        err.response?.data?.message ||
          err.message ||
          "Failed to create offramp transaction",
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Reset verification when account selection changes
  const handleAccountSelectionChange = () => {
    setVerifiedBankAccount(null);
    setBankError("");
  };

  const handleKycSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // For SDK-based session flow only (stateless flow uses dedicated KYC page)
    if (!sessionId || !session || !kycRequirement) return;

    const currentKycStep = localKycStep || "KYC_ID";
    setKycError("");

    if (kycRequirement.type === "field_based") {
      let fieldsToValidate = kycRequirement.fields ?? [];

      if (currentKycStep === "KYC_ID") {
        // Step 1: Only validate ID fields
        const idFieldTypes = ["NIN", "BVN", "ID_TYPE", "ID_NUMBER"];
        fieldsToValidate = fieldsToValidate.filter((f) =>
          idFieldTypes.includes(f.documentType),
        );

        const missing = fieldsToValidate
          .filter((f) => f.required)
          .find((f) => !kycDocs[f.documentType]);
        if (missing) {
          setKycError(`Please enter ${missing.label}.`);
          return;
        }

        // Step 1 complete: transition to Step 2 locally (don't submit yet)
        setLocalKycStep("KYC");
        setCurrentStep("KYC");
        setKycError("");
        return;
      } else if (currentKycStep === "KYC") {
        // Step 2: Validate profile fields (non-ID fields)
        const idFieldTypes = ["NIN", "BVN", "ID_TYPE", "ID_NUMBER"];
        const nameFieldTypes = [
          "FULL_NAME",
          "FIRST_NAME",
          "firstName",
          "LAST_NAME",
          "lastName",
          "NAME",
          "name",
        ];

        fieldsToValidate = fieldsToValidate.filter(
          (f) => !idFieldTypes.includes(f.documentType),
        );

        // For offramps: skip name field validation if bank account name is available
        const metadata = session?.metadata as any;
        const hasBankAccountName =
          session.type === "OFFRAMP" && metadata?.bankAccountName;
        if (hasBankAccountName) {
          fieldsToValidate = fieldsToValidate.filter(
            (f) => !nameFieldTypes.includes(f.documentType),
          );
        }

        const missing = fieldsToValidate
          .filter((f) => f.required)
          .find((f) => !kycDocs[f.documentType]);
        if (missing) {
          setKycError(`Please enter ${missing.label}.`);
          return;
        }
      }
    }

    // Submit all KYC fields at once
    setActionLoading(true);
    try {
      await sdkClient.submitKyc(sessionId, kycDocs);
      setLocalKycStep(null);
      await refreshSession();
    } catch (err: any) {
      setKycError(err.message || "KYC submission failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleKycFieldChange = (documentType: string, value: string) => {
    setKycDocs((prev) => ({ ...prev, [documentType]: value }));
  };

  const renderKycField = (field: KycField) => {
    const value = kycDocs[field.documentType] || "";

    // For offramps: auto-fill name from bank account if available
    const metadata = session?.metadata as any;
    const bankAccountName =
      session?.type === "OFFRAMP" && metadata?.bankAccountName;
    const nameFieldTypes = [
      "FULL_NAME",
      "FIRST_NAME",
      "firstName",
      "LAST_NAME",
      "lastName",
      "NAME",
      "name",
    ];
    const isNameField = nameFieldTypes.includes(field.documentType);
    const shouldAutoFill = isNameField && bankAccountName;

    if (shouldAutoFill && !value) {
      // Auto-fill name from bank account
      setKycDocs((prev) => ({
        ...prev,
        [field.documentType]: bankAccountName,
      }));
    }

    return (
      <div key={field.documentType} className="space-y-2">
        <label
          htmlFor={field.documentType}
          className="block text-sm font-medium"
        >
          {field.label}{" "}
          {field.required && <span className="text-red-500">*</span>}
        </label>

        {field.fieldType === "select" ? (
          <select
            id={field.documentType}
            value={value}
            onChange={(e) =>
              handleKycFieldChange(field.documentType, e.target.value)
            }
            className="w-full px-3 py-2 border rounded-md"
            required={field.required}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : field.fieldType === "date" ? (
          <input
            id={field.documentType}
            type="date"
            value={value}
            onChange={(e) =>
              handleKycFieldChange(field.documentType, e.target.value)
            }
            className="w-full px-3 py-2 border rounded-md"
            required={field.required}
            placeholder={field.placeholder}
          />
        ) : (
          <input
            id={field.documentType}
            type="text"
            value={shouldAutoFill && bankAccountName ? bankAccountName : value}
            onChange={(e) =>
              handleKycFieldChange(field.documentType, e.target.value)
            }
            className="w-full px-3 py-2 border rounded-md"
            required={field.required}
            placeholder={field.placeholder}
            disabled={shouldAutoFill}
          />
        )}

        {field.helpText && (
          <p className="text-xs text-gray-500">{field.helpText}</p>
        )}
      </div>
    );
  };

  const renderStepContent = () => {
    if (!currentStep) return null;

    // For pending offramp, we don't have a session yet
    const isPendingOfframp = sessionId === "pending-offramp";

    switch (currentStep) {
      case "BANK_ACCOUNT":
        return (
          <div className="swap-card rounded-2xl p-6 space-y-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
                Bank Account Details
              </h2>
              <p className="text-sm text-slate-400">
                {verifiedBankAccount
                  ? "Please confirm your account details"
                  : "Enter your bank account details to receive funds"}
              </p>
            </div>

            {bankError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                <p className="text-sm text-red-400">{bankError}</p>
              </div>
            )}

            {!verifiedBankAccount ? (
              // Step 1: Bank Account Entry & Verification
              <form onSubmit={handleVerifyBank} className="space-y-4">
                {/* Saved Accounts Selection */}
                {!showAddNew && savedBankAccounts.length > 0 && (
                  <>
                    <div className="space-y-2">
                      <label
                        htmlFor="savedAccount"
                        className="block text-xs text-slate-400 uppercase font-medium"
                      >
                        Select Saved Account
                      </label>
                      <select
                        id="savedAccount"
                        value={selectedSavedAccountId}
                        onChange={(e) => {
                          setSelectedSavedAccountId(e.target.value);
                          handleAccountSelectionChange();
                        }}
                        className="w-full bg-[var(--bg-3)] border border-[var(--border-primary)] rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                        required
                      >
                        <option value="">Choose a saved account</option>
                        {savedBankAccounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.bankName} - {account.accountNumber} (
                            {account.accountName})
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddNew(true);
                        handleAccountSelectionChange();
                      }}
                      className="text-sm text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-2"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Add a new bank account
                    </button>
                  </>
                )}

                {/* Add New Account Form */}
                {showAddNew && (
                  <>
                    {savedBankAccounts.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddNew(false);
                          setSelectedNetworkId("");
                          setAccountNumber("");
                          handleAccountSelectionChange();
                        }}
                        className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2 mb-2"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                        Use saved account
                      </button>
                    )}
                    <SearchableSelect
                      label="Select Your Bank"
                      options={bankNetworks.map((network) => ({
                        value: network.id,
                        label: network.name,
                      }))}
                      value={selectedNetworkId}
                      onChange={(value) => {
                        setSelectedNetworkId(value);
                        handleAccountSelectionChange();
                      }}
                      placeholder="Search for your bank..."
                      required
                    />

                    <div className="space-y-2">
                      <label
                        htmlFor="accountNumber"
                        className="block text-xs text-slate-400 uppercase font-medium"
                      >
                        Account Number
                      </label>
                      <input
                        id="accountNumber"
                        type="text"
                        value={accountNumber}
                        onChange={(e) => {
                          setAccountNumber(e.target.value);
                          handleAccountSelectionChange();
                        }}
                        className="w-full bg-[var(--bg-3)] border border-[var(--border-primary)] rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                        placeholder="Enter your 10-digit account number"
                        required
                      />
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  disabled={
                    isVerifyingBank ||
                    (showAddNew
                      ? !selectedNetworkId || !accountNumber
                      : !selectedSavedAccountId)
                  }
                  className="swap-button w-full py-4 rounded-2xl text-white font-bold text-lg shadow-lg shadow-purple-500/20 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
                >
                  <span className="relative z-10">
                    {isVerifyingBank ? "Verifying..." : "Verify Account"}
                  </span>
                </button>
              </form>
            ) : (
              // Step 2: Verification Result & Confirmation
              <div className="space-y-8">
                {/* Success Header */}
                <div className="text-center space-y-4">
                  <div className="inline-flex w-16 h-16 bg-green-500/20 rounded-full items-center justify-center">
                    <svg
                      className="w-8 h-8 text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-1">
                      Account Verified Successfully
                    </h3>
                    <p className="text-sm text-slate-400">
                      Please review your account details below
                    </p>
                  </div>
                </div>

                {/* Account Details Cards */}
                <div className="space-y-3">
                  {/* Bank Name Card */}
                  <div className="bg-[var(--bg-3)] border border-[var(--border-primary)] rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-purple-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500 uppercase font-medium tracking-wide mb-1">
                          Bank Name
                        </p>
                        <p className="text-base font-semibold text-white truncate">
                          {verifiedBankAccount.bankName}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Account Number Card */}
                  <div className="bg-[var(--bg-3)] border border-[var(--border-primary)] rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-blue-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500 uppercase font-medium tracking-wide mb-1">
                          Account Number
                        </p>
                        <p className="text-base font-semibold text-white font-mono">
                          {verifiedBankAccount.accountNumber}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Account Name Card */}
                  <div className="bg-[var(--bg-3)] border border-[var(--border-primary)] rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-green-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500 uppercase font-medium tracking-wide mb-1">
                          Account Name
                        </p>
                        <p className="text-base font-semibold text-white truncate">
                          {verifiedBankAccount.accountName}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Warning */}
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-yellow-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-yellow-200 leading-relaxed">
                        Please confirm this is your account before continuing. Funds will be sent to this account.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setVerifiedBankAccount(null);
                      setBankError("");
                    }}
                    className="flex-1 py-4 rounded-2xl text-white font-medium text-base border-2 border-slate-600 hover:border-slate-500 hover:bg-slate-800/30 transition-all"
                  >
                    Change Account
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmAndCreateOfframp}
                    disabled={actionLoading}
                    className="flex-1 swap-button py-4 rounded-2xl text-white font-bold text-base shadow-lg shadow-purple-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="relative z-10">
                      {actionLoading ? "Creating Transaction..." : "Confirm & Continue"}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case "KYC_ID":
        if (!kycRequirement || kycRequirement.type !== "field_based") {
          return (
            <div className="swap-card rounded-2xl p-6">
              <p className="text-slate-400">Loading KYC requirements...</p>
            </div>
          );
        }

        const idFieldTypes = ["NIN", "BVN", "ID_TYPE", "ID_NUMBER"];
        const idFields = (kycRequirement.fields ?? []).filter((f) =>
          idFieldTypes.includes(f.documentType),
        );

        return (
          <div className="swap-card rounded-2xl p-6 space-y-4">
            <h2 className="text-lg md:text-xl font-semibold text-white">
              Identity Verification - Step 1
            </h2>
            <p className="text-sm text-slate-400">
              Enter your ID number for verification
            </p>

            {kycError && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                {kycError}
              </div>
            )}

            <form onSubmit={handleKycSubmit} className="space-y-4">
              {idFields.map(renderKycField)}

              <button
                type="submit"
                disabled={actionLoading}
                className="swap-button w-full py-4 rounded-2xl text-white font-bold text-lg shadow-lg shadow-purple-500/20"
              >
                <span className="relative z-10">
                  {actionLoading ? "Submitting..." : "Continue"}
                </span>
              </button>
            </form>
          </div>
        );

      case "KYC":
        if (!kycRequirement || kycRequirement.type !== "field_based") {
          return (
            <div className="swap-card rounded-2xl p-6">
              <p className="text-slate-400">Loading KYC requirements...</p>
            </div>
          );
        }

        // For stateless flow (no sessionId), show all fields in one step
        // For SDK session flow, show only profile fields (Step 2)
        const idTypes = ["NIN", "BVN", "ID_TYPE", "ID_NUMBER"];
        const fieldsToShow = !sessionId
          ? (kycRequirement.fields ?? []) // Stateless: show all fields
          : (kycRequirement.fields ?? []).filter(
              (f) => !idTypes.includes(f.documentType),
            ); // SDK: show only profile fields

        return (
          <div className="swap-card rounded-2xl p-6 space-y-4">
            <h2 className="text-lg md:text-xl font-semibold text-white">
              {!sessionId
                ? "Identity Verification"
                : "Identity Verification - Step 2"}
            </h2>
            <p className="text-sm text-slate-400">
              {!sessionId
                ? "Please provide your information to continue"
                : "Complete your profile information"}
            </p>

            {kycError && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                {kycError}
              </div>
            )}

            <form onSubmit={handleKycSubmit} className="space-y-4">
              {fieldsToShow.map(renderKycField)}

              <button
                type="submit"
                disabled={actionLoading}
                className="swap-button w-full py-4 rounded-2xl text-white font-bold text-lg shadow-lg shadow-purple-500/20"
              >
                <span className="relative z-10">
                  {actionLoading ? "Submitting..." : "Submit"}
                </span>
              </button>
            </form>
          </div>
        );

      case "AWAIT_PAYMENT":
        // For stateless flows, use onrampResponse/offrampResponse instead of session
        const isPendingOfframp = sessionId === "pending-offramp";
        const hasOnramp = onrampResponse !== null;
        const hasOfframp = offrampResponse !== null;

        if (!session && !isPendingOfframp && !hasOnramp && !hasOfframp)
          return null;

        if (session?.type === "ONRAMP" || hasOnramp) {
          const instruction = hasOnramp
            ? onrampResponse?.paymentInstruction
            : session?.paymentInstruction;
          if (!instruction) {
            return (
              <div className="swap-card rounded-2xl p-6">
                <p className="text-slate-400">Loading payment details...</p>
              </div>
            );
          }

          // Show processing/completed screens that replace the entire payment details
          if (
            onrampStatus === "PAYMENT_RECEIVED" ||
            onrampStatus === "PAYOUT_PROCESSING" ||
            onrampStatus === "PROCESSING_PAYOUT"
          ) {
            return (
              <div className="swap-card rounded-2xl p-8 md:p-12">
                <div className="flex flex-col items-center justify-center space-y-6 text-center">
                  {/* Animated loading spinner */}
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full border-4 border-purple-500/20"></div>
                    <div className="absolute top-0 left-0 w-24 h-24 rounded-full border-4 border-transparent border-t-purple-500 animate-spin"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                      <svg
                        className="w-12 h-12 text-purple-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h2 className="text-2xl md:text-3xl font-bold text-white">
                      Processing Your Crypto
                    </h2>
                    <p className="text-slate-400 text-lg max-w-md">
                      We've received your fiat payment and are processing{" "}
                      {instruction.cryptoAmount} {instruction.cryptoCurrency} to
                      your wallet
                    </p>
                  </div>

                  <div className="w-full max-w-sm bg-slate-800/50 rounded-xl p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Amount Paid:</span>
                      <span className="text-white font-medium">
                        {instruction.localCurrency}{" "}
                        {(instruction.amountLocal?.toLocaleString() || '0')}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">You'll Receive:</span>
                      <span className="text-purple-400 font-semibold">
                        {instruction.cryptoAmount} {instruction.cryptoCurrency}
                      </span>
                    </div>
                  </div>

                  <p className="text-sm text-slate-500">
                    This usually takes a few moments...
                  </p>
                </div>
              </div>
            );
          }

          if (onrampStatus === "COMPLETED") {
            return (
              <div className="swap-card rounded-2xl p-8 md:p-12">
                <div className="flex flex-col items-center justify-center space-y-6 text-center">
                  {/* Big green checkmark */}
                  <div className="relative">
                    <div className="w-32 h-32 rounded-full bg-green-500/10 border-4 border-green-500/30 flex items-center justify-center">
                      <svg
                        className="w-20 h-20 text-green-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h2 className="text-2xl md:text-3xl font-bold text-green-400">
                      Transaction Completed!
                    </h2>
                    <p className="text-slate-400 text-lg max-w-md">
                      Your crypto has been sent successfully
                    </p>
                  </div>

                  <div className="w-full max-w-sm bg-green-500/10 border border-green-500/30 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Amount Received:</span>
                      <span className="text-green-400 font-semibold">
                        {instruction.cryptoAmount} {instruction.cryptoCurrency}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Network:</span>
                      <span className="text-white font-medium">
                        {instruction.cryptoNetwork}
                      </span>
                    </div>
                    {transactionHash && (
                      <div className="pt-3 border-t border-green-500/20">
                        <p className="text-xs text-slate-400 mb-2">
                          Transaction Hash:
                        </p>
                        {getExplorerLink(
                          transactionHash,
                          instruction.cryptoNetwork,
                        ) ? (
                          <a
                            href={
                              getExplorerLink(
                                transactionHash,
                                instruction.cryptoNetwork,
                              )!
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-mono text-green-400 hover:text-green-300 underline break-all transition-colors"
                          >
                            {transactionHash}
                          </a>
                        ) : (
                          <p className="text-xs font-mono text-slate-300 break-all">
                            {transactionHash}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div className="swap-card rounded-2xl p-6 space-y-4">
              <h2 className="text-lg md:text-xl font-semibold text-white">
                Complete Payment
              </h2>
              <p className="text-sm text-slate-400">
                Transfer the exact amount to the bank account below
              </p>

              <div className="space-y-3 bg-slate-800/50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-400">Bank Name:</span>
                  <span className="font-medium text-white text-right">
                    {instruction.bankName}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-400">Account Name:</span>
                  <span className="font-medium text-white text-right max-w-[60%] break-words">
                    {instruction.accountName}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-400">
                    Account Number:
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-medium text-white">
                      {instruction.accountNumber}
                    </span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(
                          instruction.accountNumber,
                        );
                        // Show toast or feedback
                      }}
                      className="p-1.5 hover:bg-slate-700/50 rounded transition-colors"
                      title="Copy account number"
                    >
                      <svg
                        className="w-4 h-4 text-slate-400 hover:text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center text-lg font-bold border-t border-slate-700 pt-3 mt-3">
                  <span className="text-white">Amount to Pay:</span>
                  <span className="text-white">
                    {instruction.localCurrency}{" "}
                    {(instruction.amountLocal?.toLocaleString() || '0')}
                  </span>
                </div>
              </div>

              <div className="text-sm text-slate-400">
                <p>
                  Once we receive your payment, you'll get{" "}
                  <span className="text-white font-medium">
                    {instruction.cryptoAmount} {instruction.cryptoCurrency}
                  </span>{" "}
                  on{" "}
                  <span className="text-white font-medium">
                    {instruction.cryptoNetwork}
                  </span>
                </p>
                {instruction.expiresAt && (
                  <p className="mt-2 text-slate-300">
                    ⏱️ Payment expires:{" "}
                    {new Date(instruction.expiresAt).toLocaleString()}
                  </p>
                )}
              </div>

              {/* Transaction Summary */}
              {hasOnramp && instruction.fees && (
                <div className="bg-slate-800/30 p-4 rounded-lg border border-slate-700/50 space-y-2">
                  <h3 className="text-sm font-semibold text-white mb-3">
                    Transaction Summary
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Exchange Rate:</span>
                      <span className="font-medium text-white text-right">
                        1 {instruction.cryptoCurrency} ={" "}
                        {(instruction.fxRate?.toLocaleString() || "N/A")}{" "}
                        {instruction.localCurrency}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">USD Equivalent:</span>
                      <span className="font-medium text-white">
                        ${instruction.amountUsd.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Network Fee:</span>
                      <span className="font-medium text-white">
                        {instruction.localCurrency}{" "}
                        {(instruction.fees?.networkFee?.toLocaleString() || '0')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Service Fee:</span>
                      <span className="font-medium text-white">
                        {instruction.localCurrency}{" "}
                        {(instruction.fees?.serviceFee?.toLocaleString() || '0')}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <span className="font-medium">You Will Receive:</span>
                      <span className="font-bold text-primary">
                        {instruction.cryptoAmount.toFixed(4)}{" "}
                        {instruction.cryptoCurrency}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Status indicators */}
              {/* Show countdown timer */}
              {hasOnramp &&
                timeRemaining &&
                onrampStatus !== "EXPIRED" &&
                onrampStatus !== "COMPLETED" && (
                  <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-xl text-center">
                    <p className="text-sm text-orange-400">
                      Time remaining:{" "}
                      <span className="font-mono font-bold">
                        {timeRemaining}
                      </span>
                    </p>
                  </div>
                )}

              {(onrampStatus === "PAYMENT_PENDING" ||
                onrampStatus === "PENDING") && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-blue-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-blue-400 font-semibold">
                        Waiting for payment
                      </p>
                      <p className="text-sm text-slate-400">
                        We're monitoring for your bank transfer...
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {onrampStatus === "EXPIRED" && (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-md">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-orange-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-orange-800 font-medium">
                        Session Expired
                      </p>
                      <p className="text-sm text-orange-600">
                        This payment window has closed. Please start a new
                        transaction.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {(onrampStatus === "FAILED" || onrampStatus === "KYC_FAILED") && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-red-800 font-medium">
                        Transaction Failed
                      </p>
                      <p className="text-sm text-red-600">
                        An error occurred. Please contact support.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        } else {
          // OFFRAMP
          // Use offrampResponse for stateless flow, session for SDK flow
          const instruction = isPendingOfframp
            ? offrampResponse?.cryptoInstruction
            : session?.cryptoInstruction;
          if (!instruction) {
            return (
              <div className="swap-card rounded-2xl p-6">
                <p className="text-slate-400">Loading deposit details...</p>
              </div>
            );
          }

          return (
            <div className="swap-card rounded-2xl p-6 space-y-4">
              {!isPendingOfframp && session?.status === "PAYOUT_PENDING" ? (
                // Crypto received, preparing payout
                <>
                  <div className="flex flex-col items-center justify-center py-8 space-y-4">
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center border-2 border-yellow-400">
                      <svg
                        className="w-8 h-8 text-yellow-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold">
                      Crypto received!
                    </h2>
                    <p className="text-center text-muted-foreground">
                      We've received your {instruction.cryptoAmount}{" "}
                      {instruction.cryptoCurrency}. Your payout is being
                      prepared.
                    </p>
                  </div>

                  <div className="space-y-3 bg-muted p-4 rounded-md">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">
                        Crypto Received:
                      </span>
                      <span className="font-medium">
                        {instruction.cryptoAmount} {instruction.cryptoCurrency}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">
                        Network:
                      </span>
                      <span className="font-medium">
                        {instruction.cryptoNetwork}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-3 mt-3">
                      <span>Payout Amount:</span>
                      <span>
                        {instruction.localCurrency}{" "}
                        {(instruction.amountLocal?.toLocaleString() || '0')}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-center space-x-2 text-muted-foreground">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="text-sm">Preparing your payout...</span>
                  </div>
                </>
              ) : !isPendingOfframp &&
                (session?.status === "PAYOUT_PROCESSING" ||
                  session?.status === "PROCESSING_PAYOUT") ? (
                // Processing payout to bank
                <>
                  <div className="flex flex-col items-center justify-center py-8 space-y-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center border-2 border-blue-400">
                      <svg
                        className="w-8 h-8 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold">
                      Processing payout
                    </h2>
                    <p className="text-center text-muted-foreground">
                      We're sending {instruction.localCurrency}{" "}
                      {(instruction.amountLocal?.toLocaleString() || '0')} to your bank
                      account. This usually takes a few minutes.
                    </p>
                  </div>

                  <div className="space-y-3 bg-blue-50 p-4 rounded-md border border-blue-200">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">
                        Crypto Received:
                      </span>
                      <span className="font-medium">
                        {instruction.cryptoAmount} {instruction.cryptoCurrency}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t border-blue-200 pt-3 mt-3">
                      <span>Payout Amount:</span>
                      <span className="text-blue-700">
                        {instruction.localCurrency}{" "}
                        {(instruction.amountLocal?.toLocaleString() || '0')}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-center space-x-2 text-blue-600">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="text-sm">
                      Sending to your bank account...
                    </span>
                  </div>
                </>
              ) : !isPendingOfframp && session?.status === "COMPLETED" ? (
                // Success State
                <>
                  <div className="flex flex-col items-center justify-center py-8 space-y-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-10 h-10 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-green-800">
                      Funds Sent Successfully!
                    </h2>
                    <p className="text-center text-muted-foreground">
                      {instruction.localCurrency}{" "}
                      {(instruction.amountLocal?.toLocaleString() || '0')} has been sent
                      to your bank account
                    </p>
                  </div>

                  <div className="space-y-3 bg-green-50 p-4 rounded-md border border-green-200">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">
                        Crypto Sent:
                      </span>
                      <span className="font-medium">
                        {instruction.cryptoAmount} {instruction.cryptoCurrency}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">
                        Network:
                      </span>
                      <span className="font-medium">
                        {instruction.cryptoNetwork}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t border-green-200 pt-3 mt-3">
                      <span>Amount Received:</span>
                      <span className="text-green-700">
                        {instruction.localCurrency}{" "}
                        {(instruction.amountLocal?.toLocaleString() || '0')}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate("/transactions")}
                    className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90"
                  >
                    View in Transactions
                  </button>
                </>
              ) : !isPendingOfframp && session?.status === "EXPIRED" ? (
                // Expired State
                <>
                  <div className="flex flex-col items-center justify-center py-8 space-y-4">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-10 h-10 text-orange-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-orange-800">
                      Session Expired
                    </h2>
                    <p className="text-center text-muted-foreground">
                      This deposit window has closed. Please start a new
                      transaction.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setSessionId(null);
                      setSession(null);
                      setCurrentStep(null);
                      setError("");
                      setOfframpResponse(null);
                      setSearchParams({});
                      refreshLimits(); // Refresh limits after transaction
                    }}
                    className="swap-button w-full py-4 rounded-2xl text-white font-bold text-lg shadow-lg shadow-purple-500/20"
                  >
                    <span className="relative z-10">Start New Transaction</span>
                  </button>
                </>
              ) : !isPendingOfframp &&
                (session?.status === "FAILED" ||
                  session?.status === "KYC_FAILED") ? (
                // Failed State
                <>
                  <div className="flex flex-col items-center justify-center py-8 space-y-4">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-10 h-10 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-red-800">
                      Transaction Failed
                    </h2>
                    <p className="text-center text-muted-foreground">
                      {session?.status === "KYC_FAILED"
                        ? "Verification failed. Please contact support."
                        : (session?.metadata as any)?.errorMessage ||
                          "An error occurred. Please contact support."}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setSessionId(null);
                      setSession(null);
                      setCurrentStep(null);
                      setError("");
                      setOfframpResponse(null);
                      setSearchParams({});
                      refreshLimits(); // Refresh limits after failed transaction
                    }}
                    className="swap-button w-full py-4 rounded-2xl text-white font-bold text-lg shadow-lg shadow-purple-500/20"
                  >
                    <span className="relative z-10">Try Again</span>
                  </button>
                </>
              ) : offrampStatus === "PAYMENT_RECEIVED" ||
                offrampStatus === "PAYOUT_PROCESSING" ||
                offrampStatus === "PROCESSING_PAYOUT" ||
                offrampStatus === "PROCESSING" ? (
                // Processing bank payout
                <div className="swap-card rounded-2xl p-8 md:p-12">
                  <div className="flex flex-col items-center justify-center space-y-6 text-center">
                    {/* Transaction hash first */}
                    {transactionHash && (
                      <div className="w-full max-w-sm bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                        <p className="text-xs text-slate-400 mb-2">
                          Blockchain Transaction:
                        </p>
                        {getExplorerLink(
                          transactionHash,
                          instruction.cryptoNetwork,
                        ) ? (
                          <a
                            href={
                              getExplorerLink(
                                transactionHash,
                                instruction.cryptoNetwork,
                              )!
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-mono text-green-400 hover:text-green-300 underline break-all transition-colors"
                          >
                            {transactionHash}
                          </a>
                        ) : (
                          <p className="text-xs font-mono text-slate-300 break-all">
                            {transactionHash}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Animated loading spinner */}
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full border-4 border-purple-500/20"></div>
                      <div className="absolute top-0 left-0 w-24 h-24 rounded-full border-4 border-transparent border-t-purple-500 animate-spin"></div>
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <svg
                          className="w-12 h-12 text-purple-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h2 className="text-2xl md:text-3xl font-bold text-white">
                        Processing Payment
                      </h2>
                      <p className="text-slate-400 text-lg max-w-md">
                        We've received your crypto and are processing{" "}
                        {instruction.localCurrency}{" "}
                        {(instruction.amountLocal?.toLocaleString() || '0')} to your bank
                        account
                      </p>
                    </div>

                    <div className="w-full max-w-sm bg-slate-800/50 rounded-xl p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Crypto Received:</span>
                        <span className="text-white font-medium">
                          {instruction.cryptoAmount}{" "}
                          {instruction.cryptoCurrency}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Bank Payout:</span>
                        <span className="text-purple-400 font-semibold">
                          {instruction.localCurrency}{" "}
                          {(instruction.amountLocal?.toLocaleString() || '0')}
                        </span>
                      </div>
                    </div>

                    <p className="text-sm text-slate-500">
                      This usually takes a few moments...
                    </p>
                  </div>
                </div>
              ) : offrampStatus === "COMPLETED" ? (
                // Completed - Big green checkmark
                <div className="swap-card rounded-2xl p-8 md:p-12">
                  <div className="flex flex-col items-center justify-center space-y-6 text-center">
                    {/* Big green checkmark */}
                    <div className="relative">
                      <div className="w-32 h-32 rounded-full bg-green-500/10 border-4 border-green-500/30 flex items-center justify-center">
                        <svg
                          className="w-20 h-20 text-green-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h2 className="text-2xl md:text-3xl font-bold text-green-400">
                        Transaction Completed!
                      </h2>
                      <p className="text-slate-400 text-lg max-w-md">
                        Funds have been sent to your bank account
                      </p>
                    </div>

                    <div className="w-full max-w-sm bg-green-500/10 border border-green-500/30 rounded-xl p-4 space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Amount Sent:</span>
                        <span className="text-green-400 font-semibold">
                          {instruction.localCurrency}{" "}
                          {(instruction.amountLocal?.toLocaleString() || '0')}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Crypto Sold:</span>
                        <span className="text-white font-medium">
                          {instruction.cryptoAmount}{" "}
                          {instruction.cryptoCurrency}
                        </span>
                      </div>
                      {transactionHash && (
                        <div className="pt-3 border-t border-green-500/20">
                          <p className="text-xs text-slate-400 mb-2">
                            Blockchain Transaction:
                          </p>
                          {getExplorerLink(
                            transactionHash,
                            instruction.cryptoNetwork,
                          ) ? (
                            <a
                              href={
                                getExplorerLink(
                                  transactionHash,
                                  instruction.cryptoNetwork,
                                )!
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-mono text-green-400 hover:text-green-300 underline break-all transition-colors"
                            >
                              {transactionHash}
                            </a>
                          ) : (
                            <p className="text-xs font-mono text-slate-300 break-all">
                              {transactionHash}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                // Active State - Waiting for Crypto
                <>
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl md:text-2xl font-bold text-white">
                      Send Crypto
                    </h2>
                    {timeRemaining && offrampStatus !== "EXPIRED" && (
                      <div className="flex items-center space-x-2 bg-orange-500/20 border border-orange-500/30 px-4 py-2 rounded-full">
                        <svg
                          className="w-4 h-4 text-orange-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span className="text-sm font-semibold text-orange-400">
                          {timeRemaining}
                        </span>
                      </div>
                    )}
                    {offrampStatus === "EXPIRED" && (
                      <span className="px-4 py-2 bg-red-500/20 border border-red-500/30 text-red-400 text-sm font-semibold rounded-full">
                        Expired
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-400">
                    {offrampStatus === "EXPIRED"
                      ? "This deposit window has closed. Please start a new transaction."
                      : "Send the exact crypto amount to the address below before it expires"}
                  </p>

                  {/* Status Banner */}
                  {offrampStatus === "PENDING" && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <svg
                          className="w-6 h-6 text-blue-400 animate-pulse"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-blue-400">
                          Waiting for your crypto deposit
                        </p>
                        <p className="text-xs text-slate-400">
                          Send crypto to the address above to continue
                        </p>
                      </div>
                    </div>
                  )}

                  {offrampStatus === "PAYMENT_PENDING" && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <svg
                          className="w-6 h-6 text-blue-400 animate-pulse"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-blue-400">
                          Monitoring blockchain
                        </p>
                        <p className="text-xs text-slate-400">
                          Waiting for your crypto deposit to be confirmed
                        </p>
                      </div>
                    </div>
                  )}

                  {offrampStatus === "PAYMENT_RECEIVED" && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <svg
                            className="w-6 h-6 text-green-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-green-400">
                            Crypto received!
                          </p>
                          <p className="text-xs text-slate-400">
                            Preparing your bank payout...
                          </p>
                        </div>
                      </div>
                      {transactionHash && (
                        <div className="pt-3 border-t border-green-500/20">
                          <p className="text-xs text-slate-400 font-medium mb-2">
                            Transaction Hash:
                          </p>
                          {getExplorerLink(
                            transactionHash,
                            instruction.cryptoNetwork,
                          ) ? (
                            <a
                              href={
                                getExplorerLink(
                                  transactionHash,
                                  instruction.cryptoNetwork,
                                )!
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-mono text-green-400 hover:text-green-300 underline break-all transition-colors"
                            >
                              {transactionHash}
                            </a>
                          ) : (
                            <p className="text-xs font-mono text-slate-300 break-all">
                              {transactionHash}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {offrampStatus === "PAYOUT_PENDING" && (
                    <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-md flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <svg
                          className="w-5 h-5 text-yellow-600 animate-pulse"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-yellow-800">
                          Payout pending
                        </p>
                        <p className="text-xs text-yellow-600">
                          Preparing to send funds to your bank account
                        </p>
                      </div>
                    </div>
                  )}

                  {(offrampStatus === "PAYOUT_PROCESSING" ||
                    offrampStatus === "PROCESSING_PAYOUT" ||
                    offrampStatus === "PROCESSING") && (
                    <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-md space-y-2">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <svg
                            className="w-5 h-5 text-yellow-600 animate-spin"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-yellow-800">
                            Processing payout
                          </p>
                          <p className="text-xs text-yellow-600">
                            Sending funds to your bank account...
                          </p>
                        </div>
                      </div>
                      {transactionHash && (
                        <div className="pt-2 border-t border-yellow-200">
                          <p className="text-xs text-yellow-700 font-medium mb-1">
                            Transaction Hash:
                          </p>
                          {getExplorerLink(
                            transactionHash,
                            instruction.cryptoNetwork,
                          ) ? (
                            <a
                              href={
                                getExplorerLink(
                                  transactionHash,
                                  instruction.cryptoNetwork,
                                )!
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-mono text-yellow-600 hover:text-yellow-800 underline break-all"
                            >
                              {transactionHash}
                            </a>
                          ) : (
                            <p className="text-xs font-mono text-yellow-700 break-all">
                              {transactionHash}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {offrampStatus === "COMPLETED" && (
                    <div className="bg-green-50 border border-green-200 p-3 rounded-md space-y-2">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <svg
                            className="w-5 h-5 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-green-800">
                            Transaction completed!
                          </p>
                          <p className="text-xs text-green-600">
                            Funds have been sent to your bank account
                          </p>
                        </div>
                      </div>
                      {transactionHash && (
                        <div className="pt-2 border-t border-green-200">
                          <p className="text-xs text-green-700 font-medium mb-1">
                            Blockchain Transaction:
                          </p>
                          {getExplorerLink(
                            transactionHash,
                            instruction.cryptoNetwork,
                          ) ? (
                            <a
                              href={
                                getExplorerLink(
                                  transactionHash,
                                  instruction.cryptoNetwork,
                                )!
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-mono text-green-600 hover:text-green-800 underline break-all"
                            >
                              {transactionHash}
                            </a>
                          ) : (
                            <p className="text-xs font-mono text-green-700 break-all">
                              {transactionHash}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {offrampStatus === "FAILED" && (
                    <div className="bg-red-50 border border-red-200 p-3 rounded-md flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <svg
                          className="w-5 h-5 text-red-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-red-800">
                          Transaction failed
                        </p>
                        <p className="text-xs text-red-600">
                          Please contact support for assistance
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Transaction Summary */}
                  <div className="space-y-3 bg-slate-800/50 p-4 rounded-md border border-slate-700">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        You Send:
                      </span>
                      <span className="font-semibold text-lg text-white">
                        {instruction.cryptoAmount} {instruction.cryptoCurrency}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        You Receive:
                      </span>
                      <span className="font-semibold text-lg text-green-400">
                        {instruction.localCurrency}{" "}
                        {(instruction.amountLocal?.toLocaleString() || '0')}
                      </span>
                    </div>
                    <div className="border-t border-slate-700 pt-2 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">
                          Exchange Rate:
                        </span>
                        <span className="text-slate-300">
                          1 {instruction.cryptoCurrency} ={" "}
                          {instruction.localCurrency}{" "}
                          {instruction.fxRate?.toLocaleString() || "N/A"}
                        </span>
                      </div>
                      {instruction.amountUsd && (
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">
                            USD Equivalent:
                          </span>
                          <span className="text-slate-300">
                            ${(instruction.amountUsd?.toLocaleString() || '0')}
                          </span>
                        </div>
                      )}
                      {instruction.fees?.totalFees !== undefined && (
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Fees:</span>
                          <span className="text-slate-300">
                            {instruction.localCurrency}{" "}
                            {(instruction.fees?.totalFees?.toLocaleString() || '0')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Deposit Instructions */}
                  <div className="swap-card rounded-xl p-5 space-y-4">
                    <div className="flex items-center justify-between pb-3 border-b border-slate-700">
                      <span className="text-sm text-slate-400 uppercase">
                        Network:
                      </span>
                      <span className="font-semibold text-white text-lg">
                        {instruction.cryptoNetwork}
                      </span>
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs text-slate-400 uppercase font-medium block">
                        Deposit Address
                      </label>
                      <div className="relative">
                        <div className="font-mono text-sm bg-[var(--bg-3)] border border-[var(--border-primary)] p-4 rounded-xl break-all text-white">
                          {instruction.walletAddress}
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(
                              instruction.walletAddress,
                            );
                          }}
                          className="absolute top-3 right-3 p-2 hover:bg-slate-700 rounded-lg transition-colors group"
                          title="Copy address"
                        >
                          <svg
                            className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-center py-6">
                      <div className="bg-white p-4 rounded-xl shadow-lg">
                        <QRCodeCanvas
                          value={instruction.walletAddress}
                          size={180}
                          level="H"
                          includeMargin={true}
                          className="w-full h-auto"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                      <span className="text-slate-400">Amount to Send:</span>
                      <span className="text-2xl font-bold text-purple-400">
                        {instruction.cryptoAmount} {instruction.cryptoCurrency}
                      </span>
                    </div>
                  </div>

                  {/* Important Notes */}
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-5">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-orange-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-orange-400 mb-1">
                          Important - Read Carefully
                        </p>
                        <p className="text-xs text-slate-400">
                          Please follow these instructions exactly to avoid
                          losing funds
                        </p>
                      </div>
                    </div>

                    <ul className="space-y-3">
                      <li className="flex items-start gap-3 text-sm">
                        <svg
                          className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span className="text-slate-300">
                          <strong className="text-orange-400">
                            This is a TEMPORARY address
                          </strong>{" "}
                          - it will expire in {timeRemaining || "a few minutes"}
                        </span>
                      </li>
                      <li className="flex items-start gap-3 text-sm">
                        <svg
                          className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span className="text-slate-300">
                          Send exactly{" "}
                          <strong className="text-white">
                            {instruction.cryptoAmount}{" "}
                            {instruction.cryptoCurrency}
                          </strong>{" "}
                          - no more, no less
                        </span>
                      </li>
                      <li className="flex items-start gap-3 text-sm">
                        <svg
                          className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                        <span className="text-slate-300">
                          Use the{" "}
                          <strong className="text-white">
                            {instruction.cryptoNetwork}
                          </strong>{" "}
                          network only (wrong network = lost funds)
                        </span>
                      </li>
                      <li className="flex items-start gap-3 text-sm">
                        <svg
                          className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                          />
                        </svg>
                        <span className="text-slate-300">
                          DO NOT reuse this address after expiration - create a
                          new transaction
                        </span>
                      </li>
                      <li className="flex items-start gap-3 text-sm">
                        <svg
                          className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span className="text-slate-300">
                          Funds will be sent to your bank account once we
                          confirm your crypto deposit
                        </span>
                      </li>
                      {instruction.expiresAt && (
                        <li className="flex items-start gap-3 text-sm pt-3 border-t border-orange-500/20">
                          <svg
                            className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <circle cx="12" cy="12" r="10" />
                          </svg>
                          <span className="text-red-400 font-semibold">
                            Expires at:{" "}
                            <strong className="text-red-300">
                              {new Date(instruction.expiresAt).toLocaleString()}
                            </strong>
                          </span>
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* Expired State Override */}
                  {offrampStatus === "EXPIRED" && (
                    <div className="bg-red-50 border-2 border-red-300 p-4 rounded-md">
                      <div className="flex items-center space-x-3 mb-2">
                        <svg
                          className="w-6 h-6 text-red-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <p className="text-sm font-bold text-red-800">
                          This deposit address has expired
                        </p>
                      </div>
                      <p className="text-xs text-red-700 mb-3">
                        The address above is no longer valid. Any funds sent to
                        it may be lost.
                      </p>
                      <button
                        onClick={() => navigate("/ramp")}
                        className="w-full px-4 py-2 bg-red-600 text-white rounded-md font-medium hover:bg-red-700"
                      >
                        Start New Transaction
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        }

      default:
        return null;
    }
  };

  const getProgressSteps = () => {
    if (!session) return [];

    if (session.type === "OFFRAMP") {
      return [
        { key: "BANK_ACCOUNT", label: "Bank Account" },
        { key: "KYC_ID", label: "ID Verification" },
        { key: "KYC", label: "Profile" },
        { key: "AWAIT_PAYMENT", label: "Send Crypto" },
      ];
    } else {
      return [
        { key: "KYC_ID", label: "ID Verification" },
        { key: "KYC", label: "Profile" },
        { key: "AWAIT_PAYMENT", label: "Payment" },
      ];
    }
  };

  const getCurrentStepIndex = () => {
    const steps = getProgressSteps();
    const index = steps.findIndex((s) => s.key === currentStep);
    return index >= 0 ? index : 0;
  };

  // Show loading screen while checking KYC status
  if (checkingKyc) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-[var(--bg-primary)] py-8 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto"></div>
            <p className="text-slate-400">Checking verification status...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-[var(--bg-primary)] py-8">
        <div className="max-w-7xl mx-auto space-y-8 md:px-4">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {activeTab === "ONRAMP" ? "Buy Crypto" : "Sell Crypto"}
            </h1>

            <p className="text-slate-500 text-xs mt-2">
              Click the swap button in the card to switch between Buy and Sell
            </p>
          </div>

          {/* Session Creation Form */}
          {!sessionId && !currentStep && (
            <RampCard
              mode={activeTab}
              formData={formData}
              onFormDataChange={setFormData}
              cryptoChannels={cryptoChannels}
              availableCurrencies={availableCurrencies}
              availableNetworks={availableNetworks}
              availableFiatCurrencies={availableFiatCurrencies}
              onModeSwitch={() =>
                handleTabChange(activeTab === "ONRAMP" ? "OFFRAMP" : "ONRAMP")
              }
              onSubmit={handleCreateTransaction}
              isLoading={isCreating}
              error={error}
              loadingChannels={loadingChannels}
              isInTransaction={
                !!sessionId ||
                !!onrampResponse ||
                !!offrampResponse ||
                !!currentStep
              }
            />
          )}

          {/* KYC Status Notice - Below Form */}
          {!sessionId && !currentStep && !loadingLimits && transactionLimits && kycStatus !== 'VERIFIED' && (
            <div className="max-w-3xl mx-auto mt-4">
              <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">
                        Trading with basic limits
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        ${(transactionLimits.limits?.single?.toLocaleString() || '0')} per transaction • ${(transactionLimits.limits?.daily?.toLocaleString() || '0')} daily
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/kyc')}
                    className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm font-medium rounded-lg transition-colors flex-shrink-0"
                  >
                    Unlock Higher Limits
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Flow Steps */}
          {(currentStep ||
            (sessionId && (session || sessionId === "pending-offramp")) ||
            onrampResponse ||
            offrampResponse) && (
            <div className="max-w-3xl mx-auto space-y-6">
              {/* Progress Indicator */}
              {session && (
                <div className="swap-card rounded-2xl p-6">
                  <div className="flex justify-between items-center">
                    {getProgressSteps().map((step, index) => {
                      const isActive = index === getCurrentStepIndex();
                      const isCompleted = index < getCurrentStepIndex();

                      return (
                        <div
                          key={step.key}
                          className="flex items-center flex-1"
                        >
                          <div className="flex flex-col items-center flex-1">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                isCompleted
                                  ? "bg-green-500 text-white"
                                  : isActive
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-gray-200 text-gray-500"
                              }`}
                            >
                              {isCompleted ? "✓" : index + 1}
                            </div>
                            <span
                              className={`text-xs mt-1 ${isActive ? "font-medium" : ""}`}
                            >
                              {step.label}
                            </span>
                          </div>
                          {index < getProgressSteps().length - 1 && (
                            <div
                              className={`h-0.5 flex-1 ${
                                isCompleted ? "bg-green-500" : "bg-gray-200"
                              }`}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Current Step Content */}
              {renderStepContent()}

              {/* Option to start a new transaction */}
              <div className="text-center">
                <button
                  onClick={() => {
                    setSessionId(null);
                    setSession(null);
                    setCurrentStep(null);
                    setError("");
                    setOfframpResponse(null);
                    setOnrampResponse(null);
                    setPendingOfframpData(null);
                    setOfframpStatus("PENDING");
                    setOnrampStatus("PENDING");
                    setTransactionHash("");
                    setSearchParams({}); // Clear URL params
                  }}
                  className="px-6 py-3 text-sm text-slate-400 hover:text-white transition-colors underline"
                >
                  Start a new transaction
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
